import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Link, Routes, Route } from "react-router-dom";
import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
} from "framer-motion";
import Lenis from "lenis";
import { ArrowUpRight, Database, Leaf, ShieldCheck } from "lucide-react";
import QcApp from "@/qc/QcApp";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1547936785-c57315d64694?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwzfHxib3RhbmljYWwlMjBncmVlbiUyMGxlYXZlcyUyMG1hY3JvfGVufDB8fHx8MTc5MDI0NzE5MXww&ixlib=rb-4.1.0&q=85";
const TEXTURE_IMAGE =
  "https://images.unsplash.com/photo-1528458909336-e7a0adfed0a5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwyfHxsaWdodCUyMG5hdHVyYWwlMjBsaW5lbiUyMHRleHR1cmV8ZW58MHx8fHwxNzkwMjQ3MTk4fDA&ixlib=rb-4.1.0&q=85";

const APPS = [
  {
    id: "crm",
    title: "CRM",
    description:
      "Customer relationships, accounts and order management for the sales team.",
    url: "https://crm.sunnutrafoods.com",
    host: "crm.sunnutrafoods.com",
    icon: Database,
  },
  {
    id: "quality-control",
    title: "Quality Control",
    description:
      "External lab invoicing, batch testing and product quality records.",
    url: "/qc",
    host: "Sign in · QC Portal",
    icon: ShieldCheck,
  },
];

const MARQUEE_ITEMS = [
  "High Quality Proteins",
  "Plant-Based Emulsifiers",
  "Customers Worldwide",
  "Sun Nutrafoods",
];

const EASE = [0.22, 1, 0.36, 1];

const RevealLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.9, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const AppTile = ({ app, index }) => {
  const Icon = app.icon;
  const isLive = Boolean(app.url);

  const body = (
    <>
      <div className="flex items-start justify-between">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#1C3F2B]/[0.06] text-[#1C3F2B]">
          <Icon className="h-4 w-4" strokeWidth={1.75} />
        </div>
        {isLive ? (
          <ArrowUpRight
            className="h-4 w-4 text-[#8A9A91] transition-transform duration-300 ease-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-[#1C3F2B]"
            strokeWidth={1.75}
          />
        ) : (
          <span
            data-testid={`${app.id}-coming-soon-badge`}
            className="rounded-full bg-[#E5A93D] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#1C3F2B]"
          >
            Coming Soon
          </span>
        )}
      </div>
      <h3 className="mt-6 font-display text-2xl font-semibold tracking-tight text-[#1C3F2B]">
        {app.title}
      </h3>
      <p className="mt-2 text-[13px] leading-relaxed text-[#8A9A91]">
        {app.description}
      </p>
      {isLive ? (
        <p className="mt-5 text-[10px] font-medium uppercase tracking-[0.2em] text-[#1C3F2B]/50">
          {app.host}
        </p>
      ) : (
        <p className="mt-5 text-[10px] font-medium uppercase tracking-[0.2em] text-[#8A9A91]/70">
          New app in progress
        </p>
      )}
    </>
  );

  const sharedClasses =
    "snf-tile-enter group block w-full sm:w-[224px] rounded-2xl border border-[#E8E6E1] bg-white p-5 text-left shadow-[0_8px_32px_rgba(28,63,43,0.06)]";

  const delayStyle = { animationDelay: `${1 + index * 0.12}s` };

  if (!isLive) {
    return (
      <div
        data-testid={`${app.id}-app-tile`}
        className={`${sharedClasses} cursor-not-allowed`}
        style={{ ...delayStyle, "--snf-tile-opacity": "0.7" }}
        aria-disabled="true"
      >
        {body}
      </div>
    );
  }

  if (!app.url.startsWith("http")) {
    return (
      <Link
        data-testid={`${app.id}-app-tile`}
        to={app.url}
        style={delayStyle}
        className={`${sharedClasses} transition-[transform,box-shadow] duration-300 ease-out hover:-translate-y-1.5 hover:shadow-[0_16px_48px_rgba(28,63,43,0.12)]`}
      >
        {body}
      </Link>
    );
  }

  return (
    <a
      data-testid={`${app.id}-app-tile`}
      href={app.url}
      target="_blank" rel="noopener noreferrer"
      style={delayStyle}
      className={`${sharedClasses} transition-[transform,box-shadow] duration-300 ease-out hover:-translate-y-1.5 hover:shadow-[0_16px_48px_rgba(28,63,43,0.12)]`}
    >
      {body}
    </a>
  );
};

const Marquee = () => (
  <div
    data-testid="editorial-marquee"
    className="overflow-hidden border-y border-[#E8E6E1] py-4"
  >
    <div className="snf-marquee-track flex w-max items-center">
      {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS, ...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map(
        (item, i) => (
          <span key={i} className="flex items-center">
            <span className="whitespace-nowrap font-display text-xl italic text-[#1C3F2B]/60 md:text-2xl">
              {item}
            </span>
            <Leaf
              className="mx-10 h-3.5 w-3.5 shrink-0 text-[#E5A93D]"
              strokeWidth={1.75}
            />
          </span>
        ),
      )}
    </div>
  </div>
);

const Portal = () => {
  const prefersReducedMotion = useReducedMotion();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const imageY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  useEffect(() => {
    if (prefersReducedMotion) return undefined;
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    let frame;
    const raf = (time) => {
      lenis.raf(time);
      frame = requestAnimationFrame(raf);
    };
    frame = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(frame);
      lenis.destroy();
    };
  }, [prefersReducedMotion]);

  return (
    <div
      data-testid="snf-portal"
      className="relative min-h-screen overflow-x-clip bg-[#F9F8F6] text-[#1C3F2B]"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 z-0 opacity-[0.035]"
        style={{
          backgroundImage: `url(${TEXTURE_IMAGE})`,
          backgroundSize: "1024px",
        }}
      />

      <header className="relative z-10 mx-auto flex max-w-6xl items-center justify-between px-6 py-5 md:px-8">
        <motion.img
          data-testid="header-logo"
          src="/snf-logo.png"
          alt="Sun Nutrafoods"
          className="h-[60px] w-auto md:h-[72px]"
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
        />
        <motion.span
          className="text-[11px] font-medium uppercase tracking-[0.25em] text-[#8A9A91]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          Internal Portal
        </motion.span>
      </header>

      <main className="relative z-10">
        <section
          ref={heroRef}
          className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-10 px-6 pb-10 pt-4 md:px-8 md:pt-6 lg:grid-cols-[1.15fr_0.85fr]"
        >
          <div>
            <motion.p
              data-testid="hero-overline"
              className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#8A9A91]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.15 }}
            >
              Sun Nutrafoods · Department Apps
            </motion.p>
            <h1
              data-testid="hero-heading"
              className="mt-4 font-display text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl"
            >
              <RevealLine delay={0.2}>One home for</RevealLine>
              <RevealLine delay={0.34}>
                <em className="font-medium italic">
                  every team<span className="text-[#E5A93D]">.</span>
                </em>
              </RevealLine>
            </h1>
            <motion.p
              data-testid="hero-subtext"
              className="mt-5 max-w-md text-sm leading-relaxed text-[#8A9A91] md:text-base"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.55, ease: EASE }}
            >
              Plant-based proteins and emulsifiers, delivered to customers
              worldwide. Choose a department app below — each one has its own
              sign-in.
            </motion.p>
            <motion.a
              data-testid="hero-browse-apps-link"
              href="#apps"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#1C3F2B] px-6 py-3 text-sm font-semibold text-[#F9F8F6] transition-colors duration-300 hover:bg-[#26543a]"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
            >
              Browse apps
              <ArrowUpRight className="h-4 w-4 rotate-90" strokeWidth={2} />
            </motion.a>
          </div>

          <motion.div
            className="relative mx-auto w-full max-w-[260px] lg:max-w-[320px]"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.35, ease: EASE }}
          >
            <div className="overflow-hidden rounded-t-[999px] rounded-b-2xl border border-[#E8E6E1] shadow-[0_24px_64px_rgba(28,63,43,0.14)]">
              <motion.img
                data-testid="hero-image"
                src={HERO_IMAGE}
                alt="Green botanical leaves"
                className="aspect-[4/5] w-full scale-[1.18] object-cover"
                style={prefersReducedMotion ? undefined : { y: imageY }}
              />
            </div>
            <div className="absolute -bottom-4 -left-4 rounded-xl border border-[#E8E6E1] bg-white/90 px-4 py-2.5 shadow-[0_8px_32px_rgba(28,63,43,0.10)] backdrop-blur-md">
              <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-[#8A9A91]">
                Grown by nature
              </p>
              <p className="font-display text-base italic text-[#1C3F2B]">
                Proteins &amp; emulsifiers
              </p>
            </div>
          </motion.div>
        </section>

        <section
          id="apps"
          data-testid="apps-section"
          className="mx-auto max-w-6xl scroll-mt-16 px-6 pb-14 pt-6 md:px-8"
        >
          <motion.p
            className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#8A9A91]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.9 }}
          >
            Department apps
          </motion.p>
          <motion.h2
            className="mt-3 font-display text-2xl font-semibold tracking-tight md:text-3xl"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1, ease: EASE }}
          >
            Where would you like to go?
          </motion.h2>

          <div
            data-testid="apps-grid"
            className="mt-8 flex flex-wrap justify-center gap-6 lg:justify-start"
          >
            {APPS.map((app, index) => (
              <AppTile key={app.id} app={app} index={index} />
            ))}
          </div>
        </section>

        <Marquee />
      </main>

      <footer className="relative z-10">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-6 px-6 py-10 md:flex-row md:px-8">
          <div className="flex items-center gap-3">
            <img
              src="/favicon-192x192.png"
              alt=""
              className="h-7 w-7 rounded-md"
            />
            <p className="text-sm text-[#8A9A91]">
              © {new Date().getFullYear()} Sun Nutrafoods · For internal use
            </p>
          </div>
          <a
            data-testid="footer-website-link"
            href="https://www.sunnutrafoods.com"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 rounded-full border border-[#1C3F2B]/20 px-6 py-3 text-sm font-semibold text-[#1C3F2B] transition-colors duration-300 hover:bg-[#1C3F2B] hover:text-[#F9F8F6]"
          >
            Access Company Website
            <ArrowUpRight
              className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
              strokeWidth={2}
            />
          </a>
        </div>
      </footer>
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/qc/*" element={<QcApp />} />
        <Route path="*" element={<Portal />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
